import { IReactionOptions, IReactionPublic, reaction } from 'mobx';
import { DependencyList, useEffect } from 'react';
import {
  AnyTask,
  onErrorReaction,
  onPendingReaction,
  onSuccessReaction,
  ResultAwareTask,
} from '~common/helper/mobx-tasks';

/*
 * These hooks are simply syntactic sugar to use reactions directly with hooks
 */

/**
 * Add a success reaction on mount and dispose of it at unmount
 * @param task
 * @param callback
 * @param opts
 * @param deps
 */
export const useSuccessReaction = <T>(
  task?: ResultAwareTask<T>,
  callback?: (result?: T) => void,
  opts?: IReactionOptions,
  deps: DependencyList = [],
): void => {
  useEffect((): VoidFunction => {
    const reactionDisposer = onSuccessReaction<T>(task, callback, opts);
    return (): void => {
      reactionDisposer?.();
    };
  }, [task, ...deps]);
};

/**
 * Add an error reaction on mount and dispose of it at unmount
 * @param task
 * @param callback
 * @param opts
 * @param deps
 */
export const useErrorReaction = <T extends Error>(
  task: AnyTask,
  callback?: (error?: T) => void,
  opts?: IReactionOptions,
  deps: DependencyList = [],
): void => {
  useEffect(() => {
    const reactionDisposer = onErrorReaction<T>(task, callback, opts);
    return (): void => {
      reactionDisposer?.();
    };
  }, [task, ...deps]);
};

/**
 * Add a pending reaction on mount and dispose of it at unmount
 * @param task
 * @param callback
 * @param opts
 * @param deps
 */
export const usePendingReaction = (
  task: AnyTask,
  callback?: (pending: boolean) => void,
  opts?: IReactionOptions,
  deps: DependencyList = [],
): void => {
  useEffect(() => {
    const reactionDisposer = onPendingReaction(task, callback, opts);
    return (): void => {
      reactionDisposer?.();
    };
  }, [task, ...deps]);
};

/**
 * Add a reaction on mount and dispose of it at unmount
 * @param expression
 * @param effect
 * @param opts
 * @param deps
 */
export const useReaction = <T>(
  expression: (r: IReactionPublic) => T,
  effect: (arg: T, r: IReactionPublic) => void,
  opts?: IReactionOptions,
  deps: DependencyList = [],
): void => {
  useEffect(() => {
    const reactionDisposer = reaction<T>(expression, effect, opts);
    return (): void => {
      reactionDisposer?.();
    };
  }, deps);
};



// WEBPACK FOOTER //
// /codebuild/output/src332225586/src/modules/common/src/hook/use-reaction.ts